import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { userManagementService } from '../../../services/supabaseService';

const UserStatsPanel = ({ user, onClose }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserStats();
  }, [user?.id]);

  const fetchUserStats = async () => {
    try {
      setLoading(true);
      const data = await userManagementService?.getUserStats(user?.id);
      setStats(data);
    } catch (error) {
      console.error('Error fetching user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1200] p-4">
      <div className="bg-card rounded-lg shadow-elevation-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-semibold text-lg">
                {user?.fullName?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <h2 className="text-xl font-semibold text-foreground">{user?.fullName}</h2>
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="X" size={20} />
            </button>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="py-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
              <p className="mt-4 text-muted-foreground">Loading statistics...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Activity Stats */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Activity Overview</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Trophy" size={20} className="text-primary-600" />
                      <span className="text-sm text-muted-foreground">Tournaments Joined</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stats?.tournamentsJoined || 0}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Target" size={20} className="text-green-600" />
                      <span className="text-sm text-muted-foreground">Predictions Made</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stats?.predictionsMade || 0}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Award" size={20} className="text-yellow-600" />
                      <span className="text-sm text-muted-foreground">Total Points</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stats?.totalPoints || 0}</p>
                  </div>
                  <div className="bg-muted/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={20} className="text-blue-600" />
                      <span className="text-sm text-muted-foreground">Accuracy Rate</span>
                    </div>
                    <p className="text-2xl font-bold text-foreground">{stats?.accuracyRate || 0}%</p>
                  </div>
                </div>
              </div>

              {/* Account Info */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">User ID</span>
                    <span className="text-sm font-medium text-foreground">{user?.id?.slice(0, 8)}...</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Role</span>
                    <span className="text-sm font-medium text-foreground capitalize">{user?.role}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Status</span>
                    <span className="text-sm font-medium text-foreground capitalize">{user?.status || 'active'}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-border">
                    <span className="text-sm text-muted-foreground">Member Since</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Date(user?.createdAt)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-sm text-muted-foreground">Last Updated</span>
                    <span className="text-sm font-medium text-foreground">
                      {new Date(user?.updatedAt)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              </div>

              {/* Recent Activity */}
              {stats?.recentActivity && stats?.recentActivity?.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-4">Recent Activity</h3>
                  <div className="space-y-2">
                    {stats?.recentActivity?.map((activity, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 bg-muted/30 rounded-lg">
                        <Icon name="Activity" size={16} className="text-primary-600 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-sm text-foreground">{activity?.description}</p>
                          <p className="text-xs text-muted-foreground mt-1">{activity?.timestamp}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="mt-6">
            <Button variant="outline" onClick={onClose} fullWidth>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserStatsPanel;